/**
 * PAM Overview Dashboard — read-only cross-surface status page.
 *
 * Fetches four tenant-scoped endpoints sequentially (one at a time) so that
 * each card transitions loading → ok/error independently without concurrent
 * burst hitting the /v1/admin per-IP rate-limit bucket (5 rps / 30 burst —
 * see rate_limits.go).
 *
 * Cards:
 *   1. Pending Access Requests  → /admin/access-requests
 *   2. Active Sessions          → /admin/brokered-sessions
 *   3. High-Risk Subjects       → /admin/cae
 *   4. System Health            → /admin/resilience
 *
 * OIDCPAM Gate-6 Phase 5e: migrated off MUI. This page has no single
 * cyber-iam composite target (a landing page that only links to sub-pages
 * via summary cards) — per the shared Gate-6 doc's Phase-5e directive, it is
 * composed from local themed styled-components cards (@devnw/theme tokens),
 * matching the pattern already used by the other PAM-ops pages in this gate
 * (AccessRequests.tsx, BreakGlassConsole.tsx, ...) rather than a page-shell
 * import from components/crud (that module is reserved for the generic-CRUD
 * family). All four fetches, the sequential rate-limit-friendly pacing, and
 * the per-card CardState machine are UNCHANGED.
 */
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
import type { AdminListHighRiskSubjectsResp } from '@gen/react/client'
import { useAdminGenClient } from '../api/genClient'
import { useAccessRequestsApi } from '../api/accessRequestsClient'
import { useActiveTenant } from '../layout/TenantSwitcher'
import {
  toIamResilienceStatus,
  type WireResilienceStatus,
} from '../api/resilienceMapping'

// ---------------------------------------------------------------------------
// Per-card state
// ---------------------------------------------------------------------------

type CardState<T> =
  | { status: 'loading' }
  | { status: 'ok'; value: T }
  | { status: 'error'; message: string }

// ---------------------------------------------------------------------------
// Helper — extract a human-readable message from an unknown rejection
// ---------------------------------------------------------------------------

function extractMessage(reason: unknown): string {
  if (reason instanceof Error) return reason.message
  if (typeof reason === 'string') return reason
  return 'Unknown error'
}

// ---------------------------------------------------------------------------
// Themed page/card atoms
// ---------------------------------------------------------------------------

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('lg')};
`

const PageHeading = styled.h1`
  margin: 0;
  font-size: ${({ theme }) => asUnified(theme).font.sizes.lg};
  font-weight: 600;
  color: ${({ theme }) => asUnified(theme).text.primary};
`

const Description = styled.p`
  margin: 0;
  color: ${({ theme }) => asUnified(theme).text.secondary};
`

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => ut(asUnified(theme)).space('lg')};
`

const CardLink = styled(Link)`
  display: block;
  height: 100%;
  min-width: 0;
  padding: ${({ theme }) => ut(asUnified(theme)).space('md')};
  border: 1px solid ${({ theme }) => ut(asUnified(theme)).borderColor() ?? '#e2e8f0'};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius() ?? 8}px;
  background: ${({ theme }) => asUnified(theme).bg?.surface ?? '#ffffff'};
  text-decoration: none;
  color: inherit;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => asUnified(theme).colors?.primary ?? '#1a56db'};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => asUnified(theme).colors?.primary ?? '#1a56db'};
    outline-offset: 2px;
  }
`

const CardTitle = styled.h3`
  margin: 0 0 ${({ theme }) => ut(asUnified(theme)).space('xs')};
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.sm ?? '0.875rem'};
  font-weight: 600;
  color: ${({ theme }) => asUnified(theme).text?.secondary ?? '#64748b'};
`

const CardValue = styled.p`
  margin: 0;
  font-size: ${({ theme }) =>
    `calc(${asUnified(theme).font?.sizes?.xl ?? '1.25rem'} * 1.4)`};
  font-weight: 700;
  color: ${({ theme }) => asUnified(theme).text?.primary ?? '#0f172a'};
`

const SkeletonBlock = styled.div`
  height: 48px;
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius() ?? 4}px;
  background: ${({ theme }) => asUnified(theme).bg?.subtle ?? '#e2e8f0'};

  @media (prefers-reduced-motion: no-preference) {
    animation: pam-overview-pulse 1.5s ease-in-out infinite;
  }
  @keyframes pam-overview-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`

const ErrorChip = styled.span`
  display: inline-block;
  margin-top: ${({ theme }) => ut(asUnified(theme)).space('xs')};
  padding: ${({ theme }) =>
    `${ut(asUnified(theme)).space('xxs')} ${ut(asUnified(theme)).space('sm')}`};
  border-radius: 999px;
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.xs ?? '0.75rem'};
  font-weight: 600;
  color: #ffffff;
  background: ${({ theme }) => asUnified(theme).colors?.danger ?? '#991b1b'};
`

const StatusChip = styled.span<{ $tone: 'success' | 'danger' }>`
  display: inline-block;
  margin-top: ${({ theme }) => ut(asUnified(theme)).space('xs')};
  padding: ${({ theme }) =>
    `${ut(asUnified(theme)).space('xxs')} ${ut(asUnified(theme)).space('md')}`};
  border-radius: 999px;
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.sm ?? '0.875rem'};
  font-weight: 600;
  color: #ffffff;
  background: ${({ theme, $tone }) => (
    $tone === 'danger'
      ? asUnified(theme).colors?.danger ?? '#991b1b'
      : asUnified(theme).colors?.success ?? '#166534'
  )};
`

// ---------------------------------------------------------------------------
// Card sub-component
// ---------------------------------------------------------------------------

interface OverviewCardProps {
  title: string
  to: string
  ariaLabel: string
  loading?: boolean
  children: React.ReactNode
}

const OverviewCard = ({
  title,
  to,
  ariaLabel,
  loading = false,
  children,
}: OverviewCardProps): JSX.Element => {
  return (
    <CardLink to={to} aria-label={ariaLabel} aria-busy={loading || undefined}>
      <CardTitle>{title}</CardTitle>
      {children}
    </CardLink>
  )
}

// ---------------------------------------------------------------------------
// PamOverview page
// ---------------------------------------------------------------------------

export const PamOverview = (): JSX.Element => {
  const { tenant } = useActiveTenant()
  const accessRequestsApi = useAccessRequestsApi()
  const client = useAdminGenClient()

  const [accessRequests, setAccessRequests] = useState<CardState<number>>({
    status: 'loading',
  })
  const [activeSessions, setActiveSessions] = useState<CardState<number>>({
    status: 'loading',
  })
  const [highRiskSubjects, setHighRiskSubjects] = useState<CardState<number>>({
    status: 'loading',
  })
  const [systemHealth, setSystemHealth] = useState<CardState<boolean>>({
    status: 'loading',
  })

  useEffect(() => {
    if (!tenant) return

    // Run the four fetches sequentially (one at a time) to stay within the
    // /v1/admin rate-limit bucket (rate_limits.go). Each card transitions
    // loading → ok/error independently.
    let cancelled = false

    const run = async (): Promise<void> => {
      // ── Card 1: Pending Access Requests ────────────────────────────────────
      try {
        const result = await accessRequestsApi.list(tenant)
        if (cancelled) return
        const pending = (result.items ?? []).filter(
          (r) => r.status === 'pending'
        ).length
        setAccessRequests({ status: 'ok', value: pending })
      } catch (err: unknown) {
        if (cancelled) return
        setAccessRequests({ status: 'error', message: extractMessage(err) })
      }

      // ── Card 2: Active Sessions ─────────────────────────────────────────────
      try {
        const resp = await client.adminListBrokeredSessions({
          tenantId: tenant,
          state: 'active',
        })
        if (cancelled) return
        setActiveSessions({ status: 'ok', value: (resp.sessions ?? []).length })
      } catch (err: unknown) {
        if (cancelled) return
        setActiveSessions({ status: 'error', message: extractMessage(err) })
      }

      // ── Card 3: High-Risk Subjects ──────────────────────────────────────────
      try {
        const resp: AdminListHighRiskSubjectsResp =
          await client.adminListHighRiskSubjects({
            tenantId: tenant,
            minLevel: 'high',
            limit: 100,
          })
        if (cancelled) return
        setHighRiskSubjects({
          status: 'ok',
          value: (resp.subjects ?? []).length,
        })
      } catch (err: unknown) {
        if (cancelled) return
        setHighRiskSubjects({ status: 'error', message: extractMessage(err) })
      }

      // ── Card 4: System Health ───────────────────────────────────────────────
      try {
        const resp = await client.adminPAMResilienceStatus({ tenantId: tenant })
        if (cancelled) return
        const status = toIamResilienceStatus(
          resp as unknown as WireResilienceStatus
        )
        setSystemHealth({ status: 'ok', value: status.degraded })
      } catch (err: unknown) {
        if (cancelled) return
        setSystemHealth({ status: 'error', message: extractMessage(err) })
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [tenant, client, accessRequestsApi])

  return (
    <PageContainer>
      <PageHeading>PAM Overview</PageHeading>
      <Description>
        Cross-surface read-only status. Click a card to navigate to the
        detail page.
      </Description>

      <div role="region" aria-label="PAM Status Overview">
        <CardsGrid>
          {/* ── Card 1: Pending Access Requests ─────────────────────────── */}
          <OverviewCard
            title="Pending Access Requests"
            to="/admin/access-requests"
            ariaLabel="View pending access requests"
            loading={accessRequests.status === 'loading'}
          >
            {accessRequests.status === 'loading' && (
              <SkeletonBlock data-testid="overview-skeleton" />
            )}
            {accessRequests.status === 'ok' && (
              <CardValue>{accessRequests.value}</CardValue>
            )}
            {accessRequests.status === 'error' && (
              <ErrorChip title={accessRequests.message}>Load error</ErrorChip>
            )}
          </OverviewCard>

          {/* ── Card 2: Active Sessions ──────────────────────────────────── */}
          <OverviewCard
            title="Active Sessions"
            to="/admin/brokered-sessions"
            ariaLabel="View active brokered sessions"
            loading={activeSessions.status === 'loading'}
          >
            {activeSessions.status === 'loading' && (
              <SkeletonBlock data-testid="overview-skeleton" />
            )}
            {activeSessions.status === 'ok' && (
              <CardValue>{activeSessions.value}</CardValue>
            )}
            {activeSessions.status === 'error' && (
              <ErrorChip title={activeSessions.message}>Load error</ErrorChip>
            )}
          </OverviewCard>

          {/* ── Card 3: High-Risk Subjects ───────────────────────────────── */}
          <OverviewCard
            title="High-Risk Subjects"
            to="/admin/cae"
            ariaLabel="View high-risk subjects in CAE dashboard"
            loading={highRiskSubjects.status === 'loading'}
          >
            {highRiskSubjects.status === 'loading' && (
              <SkeletonBlock data-testid="overview-skeleton" />
            )}
            {highRiskSubjects.status === 'ok' && (
              <CardValue>{highRiskSubjects.value}</CardValue>
            )}
            {highRiskSubjects.status === 'error' && (
              <ErrorChip title={highRiskSubjects.message}>
                Load error
              </ErrorChip>
            )}
          </OverviewCard>

          {/* ── Card 4: System Health ────────────────────────────────────── */}
          <OverviewCard
            title="System Health"
            to="/admin/resilience"
            ariaLabel="View PAM resilience and system health"
            loading={systemHealth.status === 'loading'}
          >
            {systemHealth.status === 'loading' && (
              <SkeletonBlock data-testid="overview-skeleton" />
            )}
            {systemHealth.status === 'ok' && (
              <StatusChip
                role="status"
                $tone={systemHealth.value ? 'danger' : 'success'}
              >
                {systemHealth.value ? 'Degraded' : 'Healthy'}
              </StatusChip>
            )}
            {systemHealth.status === 'error' && (
              <ErrorChip title={systemHealth.message}>Load error</ErrorChip>
            )}
          </OverviewCard>
        </CardsGrid>
      </div>
    </PageContainer>
  )
}
